import { RefObject, useCallback, useLayoutEffect, useRef } from 'react'

/**
 * Keeps a list's scroll offset across an in-page detail view.
 *
 * The box-truck driver consoles swap the whole list out for the detail of the row you opened, so the scroll
 * container unmounts and comes back at the top. On a fleet the operator has scrolled deep into, that threw
 * away their place on every single driver they checked — they had to scroll back down each time. The rows
 * themselves survive (they are state on the same component), so only the offset has to be carried across.
 *
 * `detailOpen` is what decides which view is showing. `onScroll` must be wired to the list container along
 * with `ref`, and it composes: whatever the page already does on scroll (infinite-scroll paging) still runs.
 *
 * `reset()` is for when the list is RE-LISTED rather than re-shown — a new search or filter starts at the top,
 * and a remembered deep offset would otherwise be restored onto a short filtered list.
 */
export const useRestoredListScroll = (
    ref: RefObject<HTMLDivElement | null>,
    detailOpen: boolean
): { onScroll: (e: React.UIEvent<HTMLDivElement>) => void; reset: () => void } => {
    const offsetRef = useRef(0)

    const onScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
        offsetRef.current = e.currentTarget.scrollTop
    }, [])

    const reset = useCallback(() => {
        offsetRef.current = 0
    }, [])

    // useLayoutEffect, not useEffect: the restore lands before the browser paints, so coming back from a
    // detail view never flashes the top of the list first.
    useLayoutEffect(() => {
        if (detailOpen) return
        if (ref.current) ref.current.scrollTop = offsetRef.current
    }, [detailOpen, ref])

    return { onScroll, reset }
}
